import React, { useState, useEffect, useLayoutEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { loginApi, googleLoginApi } from '@/utils/api'
import { GoogleLogin } from '@react-oauth/google'
import { useTranslation } from '@/i18n'
import i18n from '@/i18n/config'
import { geminiConfigManager } from '@/utils/geminiConfig'

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  // Force English language for this page before initial render
  useLayoutEffect(() => {
    i18n.changeLanguage('en-US')
  }, [])

  // Clear fields on initial load
  useEffect(() => {
    setEmail('')
    setPassword('')
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError(t('login.error.invalidCredentials'))
      setLoading(false)
      return
    }

    try {
      const response = await loginApi({
        email,
        password
      })

      if (response.code === 200) {
        console.log('Login successful:', response.data)

        // Store user info and token in localStorage
        localStorage.setItem('token', response.data.token)
        localStorage.setItem('user', JSON.stringify({
          user_id: response.data.user_id,
          nickname: response.data.nickname
        }))

        // Pre-load Gemini configs after successful login
        geminiConfigManager.fetchConfigs().catch(error => {
          console.error('Failed to pre-load Gemini configs:', error)
        })

        // Redirect to home page or the page the user was trying to access
        const from = location.state?.from?.pathname || '/'
        navigate(from, { replace: true })
      } else {
        setError(response.message || 'Login failed')
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Login error:', err)
      // Handle both string and object error responses
      const errorMessage = typeof err === 'string' ? err : err.message || 'An unexpected error occurred'
      setError(errorMessage)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8" key={JSON.stringify(location.state)}>
        {/* Logo */}
        <div className="text-center">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-600 bg-clip-text text-transparent mb-2">
            VisSynth
          </h1>
          <p className="text-gray-400">{t('login.platformDescription')}</p>
        </div>

        {/* Success Message from Signup */}
        {location.state?.message && (
          <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-sm text-green-400">
            {location.state.message}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="off">
          <div className="space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                {t('login.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="off"
                autoSave="off"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex justify-between items-center">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('login.password')}
                </label>
                <div className="text-sm">
                  <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300 transition-colors">
                    {t('login.forgotPassword')}
                  </Link>
                </div>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-950 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  {t('login.signingIn')}
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
                  </svg>
                  {t('login.login')}
                </div>
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-950 text-gray-500">{t('login.orContinueWith')}</span>
            </div>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-1 gap-3">
            <div className="w-full min-w-full"> {/* 固定最小宽度 */}
              <GoogleLogin
                locale="en-US"
                useOneTap={false} // 禁用个性化一键登录按钮，始终使用自定义按钮
                size="large" // 使用Google固定大尺寸按钮
                onSuccess={async (credentialResponse) => {
                try {
                  setLoading(true)
                  const response = await googleLoginApi({
                    id_token: credentialResponse.credential!
                  })

                  if (response.code === 200) {
                    // Store user info and token in localStorage
                    localStorage.setItem('token', response.data.token)
                    localStorage.setItem('user', JSON.stringify({
                      user_id: response.data.user_id,
                      nickname: response.data.nickname
                    }))

                    // Pre-load Gemini configs after successful login
                    geminiConfigManager.fetchConfigs().catch(error => {
                      console.error('Failed to pre-load Gemini configs:', error)
                    })

                    // Redirect to home page or the page the user was trying to access
                    const from = location.state?.from?.pathname || '/'
                    navigate(from, { replace: true })
                  } else {
                    setError(response.message || 'Google login failed')
                    setLoading(false)
                  }
                } catch (err: any) {
                  console.error('Google login error:', err)
                  const errorMessage = typeof err === 'string' ? err : err.message || 'An unexpected error occurred'
                  setError(errorMessage)
                  setLoading(false)
                }
              }}
              onError={() => {
                setError(t('login.error.googleLoginFailed'))
                setLoading(false)
              }}
            >
              {(renderProps) => (
                <button
                  onClick={renderProps.onClick}
                  disabled={renderProps.disabled}
                  className="!w-full min-w-full py-3 px-4 border border-gray-800 bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2 font-medium hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133.48 12.48.48c-8.84 0-16 7.16-16 16s7.16 16 16 16c9.235 0 15.36-6.49 15.36-15.6 0-.883-.107-1.746-.354-2.592l-1.968 1.968z" />
                  </svg>
                  Sign in with Google
                </button>
              )}
              </GoogleLogin>
            </div>
          </div>
        </form>

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-sm text-gray-400">
            {t('login.noAccount')}{' '}
            <Link to="/signup" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
              {t('login.signup')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
